/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import { Table, message, Modal, Tag, Switch } from "antd";
import { Link, useSearchParams } from 'react-router-dom';
import { medicationService, medicationCategoryService } from '../../../services/pharmacyService';
import Breadcrumb from '../../../components/ui/Breadcrumb';

const MedicationList = () => {
  const [searchParams] = useSearchParams();
  const [medications, setMedications] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [medicationToDelete, setMedicationToDelete] = useState(null);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await medicationCategoryService.getAll({ per_page: 100, is_active: true });
      setCategories(response.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  const fetchMedications = useCallback(async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const response = await medicationService.getAll({
        page,
        per_page: pageSize,
        search: searchTerm || undefined,
        category_id: categoryFilter || undefined
      });

      setMedications((response.data || []).map(m => ({
        id: m.id,
        name: m.name,
        code: m.code,
        active_ingredient: m.active_ingredient,
        category_name: m.category?.name || 'N/A',
        unit_name: m.unit?.abbreviation || 'N/A',
        presentation: m.presentation,
        concentration: m.concentration,
        min_stock: m.min_stock,
        current_stock: m.current_stock,
        requires_prescription: m.requires_prescription,
        is_active: m.is_active,
      })));
      setTotal(response.total || 0);
    } catch (error) {
      console.error('Error fetching medications:', error);
      message.error('Error al cargar medicamentos');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, categoryFilter]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchMedications(pagination.current, pagination.pageSize);
  }, [fetchMedications, pagination.current, pagination.pageSize]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchMedications(1, pagination.pageSize);
    setPagination({ ...pagination, current: 1 });
  };

  const handleDelete = async () => {
    if (!medicationToDelete) return;
    try {
      await medicationService.delete(medicationToDelete.id);
      message.success('Medicamento eliminado correctamente');
      setDeleteModalVisible(false);
      setMedicationToDelete(null);
      fetchMedications(pagination.current, pagination.pageSize);
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Error al eliminar medicamento';
      message.error(errorMsg);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  const columns = [
    {
      title: "Nombre",
      dataIndex: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) => (
        <Link to={`/edit-medication?id=${record.id}`}>{text}</Link>
      )
    },
    {
      title: "Código",
      dataIndex: "code",
    },
    {
      title: "Principio Activo",
      dataIndex: "active_ingredient",
    },
    {
      title: "Categoría",
      dataIndex: "category_name",
    },
    {
      title: "Presentación",
      dataIndex: "presentation",
      render: (text) => text || 'N/A'
    },
    {
      title: "Stock",
      dataIndex: "current_stock",
      render: (stock, record) => (
        <span style={{ color: stock <= record.min_stock ? 'red' : 'inherit' }}>
          {stock} / {record.min_stock}
        </span>
      )
    },
    {
      title: "Receta",
      dataIndex: "requires_prescription",
      render: (req) => (
        <Tag color={req ? 'orange' : 'green'}>
          {req ? 'Sí' : 'No'}
        </Tag>
      )
    },
    {
      title: "Estado",
      dataIndex: "is_active",
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Activo' : 'Inactivo'}
        </Tag>
      )
    },
    {
      title: "",
      dataIndex: "actions",
      render: (text, record) => (
        <div className="text-end">
          <div className="dropdown dropdown-action">
            <Link
              to="#"
              className="action-icon dropdown-toggle"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="fas fa-ellipsis-v" />
            </Link>
            <div className="dropdown-menu dropdown-menu-end">
              <Link className="dropdown-item" to={`/edit-medication?id=${record.id}`}>
                <i className="far fa-edit me-2" />
                Editar
              </Link>
              <button
                className="dropdown-item"
                onClick={() => {
                  setMedicationToDelete(record);
                  setDeleteModalVisible(true);
                }}
              >
                <i className="far fa-trash-alt me-2" />
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <>
          <div className="page-header">
            <div className="row align-items-center">
              <div className="col">
                <h3 className="page-title">Medicamentos</h3>
                <Breadcrumb items={[{ label: 'Inicio', to: '/' }, { label: 'Medicamentos' }]} />
              </div>
              <div className="col-auto text-end float-end ms-auto">
                <Link to="/add-medication" className="btn btn-primary">
                  <i className="fas fa-plus me-2" />
                  Agregar Medicamento
                </Link>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title">Lista de Medicamentos</h5>
                </div>
                <div className="card-body">
                  <div className="row mb-3">
                    <div className="col-md-3">
                      <form onSubmit={handleSearch}>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Buscar..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          <button type="submit" className="btn btn-primary">
                            <i className="fas fa-search" />
                          </button>
                        </div>
                      </form>
                    </div>
                    <div className="col-md-3">
                      <select
                        className="form-select"
                        value={categoryFilter || ''}
                        onChange={(e) => {
                          setCategoryFilter(e.target.value ? parseInt(e.target.value) : null);
                          setPagination({ ...pagination, current: 1 });
                        }}
                      >
                        <option value="">Todas las categorías</option>
                        {categories.map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <Table
                    columns={columns}
                    dataSource={medications}
                    loading={loading}
                    rowKey="id"
                    pagination={{
                      ...pagination,
                      total: total,
                      showSizeChanger: true,
                      showTotal: (total) => `Total ${total} registros`
                    }}
                    onChange={handleTableChange}
                  />
                </div>
              </div>
            </div>
          </div>

          <Modal
            title="Confirmar Eliminación"
            open={deleteModalVisible}
            onOk={handleDelete}
            onCancel={() => {
              setDeleteModalVisible(false);
              setMedicationToDelete(null);
            }}
            okText="Eliminar"
            okButtonProps={{ danger: true }}
          >
            <p>
              ¿Está seguro de eliminar el medicamento <strong>{medicationToDelete?.name}</strong>?
            </p>
          </Modal>
    </>
  );
};

export default MedicationList;
